import { observer } from 'mobx-react-lite';
import { useRef, useEffect } from 'react';
import ProductMenuItem from '@features/navigation/components/ProductMenuItem/ProductMenuItem';
import styles from '@features/navigation/components/Navigation/Navigation.styles';
import { ICategory } from '@features/categories';

type PropsType = {
  products: ICategory[];
  color: string;
  onClick: (product: ICategory) => void;
  selectedGroup: ICategory | null;
  onClose?: () => void;
};

const ProductMenuDesktop = ({ products, color, onClick, selectedGroup, onClose }: PropsType) => {
  const menuRef = useRef<HTMLUListElement>(null);
  // Keyboard navigation - WCAG 2.1.1 Keyboard, 2.4.3 Focus Order
  useEffect(() => {
    // Get all buttons inside menuitems (actual focusable elements)
    const getMenuButtons = (): HTMLElement[] => {
      if (!menuRef.current) return [];
      return Array.from(menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"] button'));
    };

    const menuButtons = getMenuButtons();
    if (menuButtons.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Arrow keys, Enter, Space, and Escape
      // Tab should work normally - we don't intercept it
      if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End', 'Escape', 'Enter', ' '].includes(e.key)) {
        return;
      }

      // Only handle if focus is within this menu
      if (!menuRef.current?.contains(document.activeElement)) return;

      const currentIndex = menuButtons.findIndex((btn) => btn === document.activeElement);

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = currentIndex < menuButtons.length - 1 ? currentIndex + 1 : 0;
          menuButtons[nextIndex]?.focus();
          // Open menu on focus
          const product = products[nextIndex];
          if (product) onClick(product);
          break;
        }
        case 'ArrowLeft':
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuButtons.length - 1;
          menuButtons[prevIndex]?.focus();
          // Open menu on focus
          const product = products[prevIndex];
          if (product) onClick(product);
          break;
        }
        case 'Home': {
          e.preventDefault();
          menuButtons[0]?.focus();
          const product = products[0];
          if (product) onClick(product);
          break;
        }
        case 'End': {
          e.preventDefault();
          const lastIndex = menuButtons.length - 1;
          menuButtons[lastIndex]?.focus();
          const product = products[lastIndex];
          if (product) onClick(product);
          break;
        }
        case 'Enter':
        case ' ': {
          // Enter or Space opens the menu for the focused item
          e.preventDefault();
          const product = products[currentIndex];
          if (product) onClick(product);
          break;
        }
        case 'Escape': {
          e.preventDefault();
          if (onClose) onClose();
          break;
        }
      }
    };

    menuRef.current?.addEventListener('keydown', handleKeyDown);
    return () => {
      menuRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [products, onClick, onClose]);

  return (
    <ul ref={menuRef} sx={styles.productMenuContainer} role="menubar" aria-label="Product navigation">
      {products.map((product) => {
        const isActive = selectedGroup?.id === product.id;
        return (
          <ProductMenuItem
            key={product.id}
            product={product}
            color={color}
            isMobile={false}
            onClick={onClick}
            isActive={isActive}
            cy={`Desktop-MenuItem-${product.slug}`}
          />
        );
      })}
    </ul>
  );
};

export default observer(ProductMenuDesktop);
